import { Inverter, Range } from "../../telemetry";
import {
  getFirstAndLastWeekDays,
  selectedFormat,
  MathAvgWithValuesDifferentToZero,
  minElementToNull,
  MathSomme,
  groupByCount,
  getTimeRemoveAndApplyOffset,
  removeNullToSeries,
} from "../../utils";
import { EnergyDetailsSeries, EnergySeries, EnergySeriesWithZeros } from "../model";
import { applyColors, applyEnergiesColors } from "../styles.utils";

const initEnergySeries = (name: string) => ({ values: [[]], totAvg: 0, totMax: 0, totMin: 0, name, maxValue: [] });

export interface GraphEnergyHelper {
  periodDates: string[];
  applyColors(): void;
  getMonthInWeeksSeries(): EnergyDetailsSeries[];
  getEnergies(): EnergySeries;
  getGraphFnOptions(buildEnergyPeriodGraph: any, buildEnergyOverviewGraph?: any): { chartOptionsFn: any; overviewChartOptionsFn?: any; series: EnergySeries | undefined };
}

export interface InverterAggregate {
  [key: string]: (number | null)[];
}

export class GraphEnergyWeekHelper implements GraphEnergyHelper {
  weeksData?: EnergySeries;
  monthOverviewData?: EnergySeries;
  periodDates: string[] = [];
  monthDates: string[] = [];
  private readonly groupByDays = 7;

  constructor(readonly series: Partial<Inverter>[], range: Range) {
    /**start & end of month f.e. `1st` till `31st` */
    const monthTo = new Date(range.to);
    const monthFrom =  new Date(range.from);
     /**start & end of month divided into full weeks */
    const weeksTo = new Date(getFirstAndLastWeekDays(range.to).to);
    const weeksFrom = new Date(getFirstAndLastWeekDays(range.from).from);

    let telemetryMonthInWeeks: InverterAggregate = {};
    let telemetryOverviewMonth: InverterAggregate = {};
    for (const d = weeksFrom; d <= weeksTo; d.setDate(d.getDate() + 1)) {
      const date = selectedFormat(d);
      this.periodDates.push(date);
      const currentData = !!series ? series.find((data) => {
        if (data.TS) {
          // TODO: Qui viene rimossa l'offset aggiunto dall'ora locale (serve applicare la timezone del sistema)
          const currentDate = selectedFormat(getTimeRemoveAndApplyOffset(data.TS, 0));
          return currentDate === date
        }
        return false;
      }) : {};
      telemetryMonthInWeeks = {
        EG1: [...(telemetryMonthInWeeks.EG1 || []), Number(currentData?.EG1) || null],
        // EG2 is handled as negative -> export from my system
        EG2: [...(telemetryMonthInWeeks.EG2 || []), - Number(currentData?.EG2) || null],
        EG3: [...(telemetryMonthInWeeks.EG3 || []), Number(currentData?.EG3) || null],
        EG4: [...(telemetryMonthInWeeks.EG4 || []), Number(currentData?.EG4) || null],
        EG5: [...(telemetryMonthInWeeks.EG5 || []), - Number(currentData?.EG5) || null],
        // EG6 is handled as negative -> discharging from my battery
        EG6: [...(telemetryMonthInWeeks.EG6 || []), Number(currentData?.EG6) || null],
      };
    }

    for (const d = monthFrom; d <= monthTo; d.setDate(d.getDate() + 1)) {
      const date = selectedFormat(d);
      this.monthDates.push(date);
      const currentData = !!series ? series.find((data) => {
        if (data.TS) {
          // TODO: Qui viene rimossa l'offset aggiunto dall'ora locale (serve applicare la timezone del sistema)
          const currentDate = selectedFormat(getTimeRemoveAndApplyOffset(data.TS, 0));
          return currentDate === date
        }
        return false;
      }) : {};
      telemetryOverviewMonth = {
        EG1: [...(telemetryOverviewMonth.EG1 || []), Number(currentData?.EG1) || null],
        // EG2 is handled as negative -> export from my system
        EG2: [...(telemetryOverviewMonth.EG2 || []), - Number(currentData?.EG2) || null],
        EG3: [...(telemetryOverviewMonth.EG3 || []), Number(currentData?.EG3) || null],
        EG4: [...(telemetryOverviewMonth.EG4 || []), Number(currentData?.EG4) || null],
        EG5: [...(telemetryOverviewMonth.EG5 || []), - Number(currentData?.EG5) || null],
        // EG6 is handled as negative -> discharging from my battery
        EG6: [...(telemetryOverviewMonth.EG6 || []), Number(currentData?.EG6) || null],
      };
    }

    const telemetryByWeeks: EnergySeries = {};
    const telemetryByMonthOverview: EnergySeries = {};
    const telemetryAttributeNames = ['EG1', 'EG2', 'EG3', 'EG4', 'EG5' ,'EG6'];
    for (const name of telemetryAttributeNames) {
      const weekValues = groupByCount(telemetryMonthInWeeks[name], this.groupByDays);
      telemetryByWeeks[name] = {
        name,
        values: weekValues,
        maxValue: weekValues.map((weekValues) => Math.max(...weekValues)),
        totMin: Math.min(...(telemetryMonthInWeeks[name].filter((value): value is number => value !== null))),
        totMax: Math.max(...(telemetryMonthInWeeks[name].filter((value): value is number => value !== null))),
        totAvg: MathAvgWithValuesDifferentToZero(telemetryMonthInWeeks[name].filter((value): value is number => value !== null)),
      };

      const monthValues = [telemetryOverviewMonth[name]];
      telemetryByMonthOverview[name] = {
        name,
        values: monthValues,
        maxValue: weekValues.map((weekValues) => Math.max(...weekValues)),
        totMin: Math.min(...(telemetryOverviewMonth[name].filter((value): value is number => value !== null))),
        totMax: Math.max(...(telemetryOverviewMonth[name].filter((value): value is number => value !== null))),
        totAvg: MathAvgWithValuesDifferentToZero(telemetryOverviewMonth[name].filter((value): value is number => value !== null)),
      };
    }

    this.weeksData = telemetryByWeeks;
    this.monthOverviewData = telemetryByMonthOverview;
  }

  applyColors() {
    this.weeksData = applyColors(this.weeksData);
    this.monthOverviewData = applyColors(this.monthOverviewData);
  }

  getMonthInWeeksSeries() {
    if (!this.weeksData) {
      throw new Error('Missing telemetry');
    }
    this.applyColors();
    return Object.values(this.weeksData);
  }

  getMonthOverviewSeries() {
    if (!this.monthOverviewData) {
      throw new Error('Missing telemetry');
    }
    this.applyColors();
    return Object.values(this.monthOverviewData);
  }

  getEnergies() {
    if (!this.weeksData) {
      throw new Error('Missing telemetry');
    }
    const energiesSeries: Partial<EnergySeries> = {};
    const { EG1, EG2, EG3, EG4, EG5, EG6 } = removeNullToSeries(this.weeksData);

    EG1.values.forEach((_, i) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      energiesSeries.consume === undefined ? (energiesSeries.consume = initEnergySeries('consume')) : energiesSeries.consume.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      energiesSeries.selfConsume === undefined
        ? (energiesSeries.selfConsume = initEnergySeries('selfConsume'))
        : energiesSeries.selfConsume.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      energiesSeries.fromGrid === undefined ? (energiesSeries.fromGrid = initEnergySeries('fromGrid')) : energiesSeries.fromGrid.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      energiesSeries.production === undefined ? (energiesSeries.production = initEnergySeries('production')) : energiesSeries.production.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      energiesSeries.selfProduction === undefined ? (energiesSeries.selfProduction = initEnergySeries('selfProduction')) : energiesSeries.selfProduction.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      energiesSeries.fromSolar === undefined ? (energiesSeries.fromSolar = initEnergySeries('fromSolar')) : energiesSeries.fromSolar.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      energiesSeries.toHome === undefined ? (energiesSeries.toHome = initEnergySeries('toHome')) : energiesSeries.toHome.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      energiesSeries.toGrid === undefined ? (energiesSeries.toGrid = initEnergySeries('toGrid')) : energiesSeries.toGrid.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      energiesSeries.toBattery === undefined ? (energiesSeries.toBattery = initEnergySeries('toBattery')) : energiesSeries.toBattery.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      energiesSeries.fromBattery === undefined
        ? (energiesSeries.fromBattery = initEnergySeries('fromBattery'))
        : energiesSeries.fromBattery.values.push([]);
      _.forEach((__, j) => {
        const valueEG3 = EG3.values[i][j];
        const valueEG4 = EG4.values[i][j];
        const valueEG6 = EG6.values[i][j];
        const valueEG2 = EG2.values[i][j];
        const valueEG5 = EG5.values[i][j];
        const valueEG1 = EG1.values[i][j];
        if (energiesSeries.consume && energiesSeries.selfProduction &&energiesSeries.fromGrid && energiesSeries.selfConsume && energiesSeries.fromSolar && energiesSeries.fromBattery && energiesSeries.toBattery && energiesSeries.toHome && energiesSeries.toGrid && energiesSeries.production) {
          // from @Mastronardi
          // self-consume = home - gridS
          // from battery = BatteryS
          // from solar = home - gridS - BatteryS
          energiesSeries.consume.values[i][j] = minElementToNull(valueEG4);
          energiesSeries.fromGrid.values[i][j] = minElementToNull(valueEG3);
          energiesSeries.selfConsume.values[i][j] = minElementToNull(Number(valueEG4 || null) - Number(valueEG3 || null));
          energiesSeries.fromSolar.values[i][j] = minElementToNull(Number(valueEG4 || null) - Number(valueEG3 || null) - Number(valueEG6 || null));
          // fromBattery can be different from zero although consumption is zero

          const toBattery = minElementToNull(Number(valueEG5 || 0) > 0 ? Number(valueEG5 || null) : -Number(valueEG5 || null));
          const toGrid = minElementToNull(Number(valueEG2 || 0) > 0 ? Number(valueEG2 || null) : -Number(valueEG2 || null));
          energiesSeries.fromBattery.values[i][j] = minElementToNull(Number(valueEG4 || null) > 0 ? Number(valueEG6 || null) : 0);
          energiesSeries.toBattery.values[i][j] = toBattery;
          energiesSeries.toGrid.values[i][j] = toGrid;
          energiesSeries.toHome.values[i][j] = minElementToNull(Number(valueEG1 || null) - ((toGrid || 0) + (toBattery || 0)));
          energiesSeries.selfProduction.values[i][j] =  minElementToNull(Number(valueEG1 || null) - (toGrid || 0));
          energiesSeries.production.values[i][j] = minElementToNull(Number(valueEG1 || null));
        }
      });
      energiesSeries.consume.maxValue.push(Math.max(...energiesSeries.consume.values[i].filter((x: number | null) => x !== null) as number[]));
      energiesSeries.fromGrid.maxValue.push(Math.max(...energiesSeries.fromGrid.values[i].filter((x: number | null) => x !== null) as number[]));
      energiesSeries.selfConsume.maxValue.push(Math.max(...energiesSeries.selfConsume.values[i].filter((x: number | null) => x !== null) as number[]));
      energiesSeries.fromSolar.maxValue.push(Math.max(...energiesSeries.fromSolar.values[i].filter((x: number | null) => x !== null) as number[]));
      energiesSeries.fromBattery.maxValue.push(Math.max(...energiesSeries.fromBattery.values[i].filter((x: number | null) => x !== null) as number[]));
    });
    if (energiesSeries.production) {
      const flat = energiesSeries.production.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      energiesSeries.production.totMax = Math.max(...energiesSeries.production.maxValue);
      energiesSeries.production.total = MathSomme(flat);
      energiesSeries.production.totMin = Math.min(...flat);
      energiesSeries.production.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }
    if (energiesSeries.consume) {
      const flat = energiesSeries.consume.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      energiesSeries.consume.totMax = Math.max(...energiesSeries.consume.maxValue);
      energiesSeries.consume.totMin = Math.min(...flat);
      energiesSeries.consume.total = MathSomme(flat);
      energiesSeries.consume.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }
    if (energiesSeries.toHome) {
      const flat = energiesSeries.toHome.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      if (energiesSeries.production?.total) {
        energiesSeries.toHome.selfConsumePercentage = Number((MathSomme(flat) /  energiesSeries.production.total).toFixed(3));
        energiesSeries.toHome.selfConsumePercentage = energiesSeries.toHome.selfConsumePercentage > 1
          ? 1 : energiesSeries.toHome.selfConsumePercentage;
      }
      energiesSeries.toHome.totMax = Math.max(...energiesSeries.toHome.maxValue);
      energiesSeries.toHome.totMin = Math.min(...flat);
      energiesSeries.toHome.totAvg = MathAvgWithValuesDifferentToZero(flat);
      energiesSeries.toHome.total = MathSomme(flat);
    }
    if (energiesSeries.toBattery) {
      const flat = energiesSeries.toBattery.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      energiesSeries.toBattery.totMax = Math.max(...energiesSeries.toBattery.maxValue);
      energiesSeries.toBattery.totMin = Math.min(...flat);
      energiesSeries.toBattery.totAvg = MathAvgWithValuesDifferentToZero(flat);
      energiesSeries.toBattery.total = MathSomme(flat);
      if (energiesSeries.production?.total) {
        energiesSeries.toBattery.selfConsumePercentage = Number((MathSomme(flat) /  energiesSeries.production.total).toFixed(3));
        energiesSeries.toBattery.selfConsumePercentage = energiesSeries.toBattery.selfConsumePercentage > 1
          ? 1 : energiesSeries.toBattery.selfConsumePercentage;
      }
    }
    if (energiesSeries.toGrid) {
      const flat = energiesSeries.toGrid.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      if (energiesSeries.production?.total) {
        energiesSeries.toGrid.selfConsumePercentage = Number((MathSomme(flat) /  energiesSeries.production.total).toFixed(3));
        energiesSeries.toGrid.selfConsumePercentage = energiesSeries.toGrid.selfConsumePercentage > 1
          ? 1 : energiesSeries.toGrid.selfConsumePercentage;
      }
      energiesSeries.toGrid.totMax = Math.max(...energiesSeries.toGrid.maxValue);
      energiesSeries.toGrid.totMin = Math.min(...flat);
      energiesSeries.toGrid.totAvg = MathAvgWithValuesDifferentToZero(flat);
      energiesSeries.toGrid.total = MathSomme(flat);
    }
    if (energiesSeries.fromGrid) {
      const flat = energiesSeries.fromGrid.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      energiesSeries.fromGrid.totMax = Math.max(...energiesSeries.fromGrid.maxValue);
      energiesSeries.fromGrid.totMin = Math.min(...flat);
      energiesSeries.fromGrid.total = MathSomme(flat);
      if (energiesSeries.consume?.total) {
        energiesSeries.fromGrid.selfConsumePercentage = Number((MathSomme(flat) /  energiesSeries.consume.total).toFixed(3));
        energiesSeries.fromGrid.selfConsumePercentage = energiesSeries.fromGrid.selfConsumePercentage > 1
          ? 1 : energiesSeries.fromGrid.selfConsumePercentage;
      }
      energiesSeries.fromGrid.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }
    if (energiesSeries.selfProduction) {
      const flat = energiesSeries.selfProduction.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      energiesSeries.selfProduction.totMax = Math.max(...energiesSeries.selfProduction.maxValue);
      energiesSeries.selfProduction.totMin = Math.min(...flat);
      energiesSeries.selfProduction.total = MathSomme(flat);
      if (energiesSeries.production?.total) {
        energiesSeries.selfProduction.selfConsumePercentage = Number((MathSomme(flat) /  energiesSeries.production.total).toFixed(3));
        energiesSeries.selfProduction.selfConsumePercentage = energiesSeries.selfProduction.selfConsumePercentage > 1
          ? 1 : energiesSeries.selfProduction.selfConsumePercentage;
      }
      energiesSeries.selfProduction.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }
    if (energiesSeries.selfConsume) {
      const flat = energiesSeries.selfConsume.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      energiesSeries.selfConsume.totMax = Math.max(...energiesSeries.selfConsume.maxValue);
      energiesSeries.selfConsume.total = MathSomme(flat);
      if (energiesSeries.consume && energiesSeries.consume.total) {
        energiesSeries.selfConsume.selfConsumePercentage = Number((MathSomme(flat) /  energiesSeries.consume.total).toFixed(3));
        energiesSeries.selfConsume.selfConsumePercentage = energiesSeries.selfConsume.selfConsumePercentage > 1
          ? 1 : energiesSeries.selfConsume.selfConsumePercentage;
      }
      energiesSeries.selfConsume.totMin = Math.min(...flat);
      energiesSeries.selfConsume.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }
    if (energiesSeries.fromSolar) {
      const flat = energiesSeries.fromSolar.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      energiesSeries.fromSolar.totMax = Math.max(...energiesSeries.fromSolar.maxValue);
      energiesSeries.fromSolar.total = MathSomme(flat);
      if (energiesSeries.consume?.total) {
        energiesSeries.fromSolar.selfConsumePercentage = Number((MathSomme(flat) /  energiesSeries.consume.total).toFixed(3));
        energiesSeries.fromSolar.selfConsumePercentage = energiesSeries.fromSolar.selfConsumePercentage > 1
          ? 1 : energiesSeries.fromSolar.selfConsumePercentage;
      }
      energiesSeries.fromSolar.totMin = Math.min(...flat);
      energiesSeries.fromSolar.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }
    if (energiesSeries.fromBattery) {
      const flat = energiesSeries.fromBattery.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      energiesSeries.fromBattery.totMax = Math.max(...energiesSeries.fromBattery.maxValue);
      energiesSeries.fromBattery.total = MathSomme(flat);
      if (energiesSeries.consume?.total) {
        energiesSeries.fromBattery.selfConsumePercentage = Number((MathSomme(flat) /  energiesSeries.consume.total).toFixed(3));
        energiesSeries.fromBattery.selfConsumePercentage = energiesSeries.fromBattery.selfConsumePercentage > 1
          ? 1 : energiesSeries.fromBattery.selfConsumePercentage;
      }
      energiesSeries.fromBattery.totMin = Math.min(...flat);
      energiesSeries.fromBattery.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }

    return applyEnergiesColors(energiesSeries);
  }

  getGraphFnOptions(buildEnergyWeekGraph: any, buildEnergyMonthGraph?: any) {
    return {
      chartOptionsFn: buildEnergyWeekGraph(this.getMonthInWeeksSeries(), this.periodDates),
      overviewChartOptionsFn: buildEnergyMonthGraph ? buildEnergyMonthGraph(this.getMonthOverviewSeries(), this.monthDates) : undefined,
      series: this.weeksData,
    };
  }
}

export class GraphEnergyMonthHelper implements GraphEnergyHelper {
  semestersData?: EnergySeries;
  periodDates: string[] = [];

  yearOverviewData?: EnergySeries;
  yearDates: string[] = [];

  constructor(readonly series: Partial<Inverter>[], range: Range) {
    const to = new Date(range.to);
    const from = new Date(range.from);
    let semesterTelemetry = {} as any;
    let yearTelemetry = {} as any;
    for (const d = from; d <= to; d.setMonth(d.getMonth() + 1)) {
      const date = selectedFormat(d);
      this.periodDates.push(date);
      this.yearDates.push(date)
      // All data of current month
      const currentData = (!!series ? series.filter((data) => {
        if (data.TS !== undefined) {
          // TODO: va considerata la timezone cone nel caso sopra Energy Week Helper
          const currentDataMonth = (new Date(data.TS * 1000)).getMonth();
          return d.getMonth() === currentDataMonth;
        }
        return false;
      }) : []).reduce((prev, curr)=>({
        EG1: (Number(prev.EG1) || 0) + (Number(curr.EG1) || 0),
        EG2: (Number(prev.EG2) || 0) + (Number(curr.EG2) || 0),
        EG3: (Number(prev.EG3) || 0) + (Number(curr.EG3) || 0),
        EG4: (Number(prev.EG4) || 0) + (Number(curr.EG4) || 0),
        EG5: (Number(prev.EG5) || 0) + (Number(curr.EG5) || 0),
        EG6: (Number(prev.EG6) || 0) + (Number(curr.EG6) || 0),
      }),{} as any);
      semesterTelemetry = {
        EG1: [...(semesterTelemetry.EG1 || []), Number(currentData?.EG1) || null],
        // EG2 is handled as negative -> export from my system
        EG2: [...(semesterTelemetry.EG2 || []), - Number(currentData?.EG2) || null],
        EG3: [...(semesterTelemetry.EG3 || []), Number(currentData?.EG3) || null],
        EG4: [...(semesterTelemetry.EG4 || []), Number(currentData?.EG4) || null],
        EG5: [...(semesterTelemetry.EG5 || []), - Number(currentData?.EG5) || null],
        // EG6 is handled as negative -> discharging from my battery
        EG6: [...(semesterTelemetry.EG6 || []), Number(currentData?.EG6) || null],
      };
    }

    for (const d = from; d <= to; d.setMonth(d.getMonth() + 1)) {
      const date = selectedFormat(d);
      this.periodDates.push(date);
      // All data of current month
      const currentData = (!!series ? series.filter((data) => {
        if (data.TS !== undefined) {
          // TODO: va considerata la timezone cone nel caso sopra Energy Week Helper
          const currentDataMonth = (new Date(data.TS * 1000)).getMonth();
          return d.getMonth() === currentDataMonth;
        }
        return false;
      }) : []).reduce((prev, curr)=>({
        EG1: (Number(prev.EG1) || 0) + (Number(curr.EG1) || 0),
        EG2: (Number(prev.EG2) || 0) + (Number(curr.EG2) || 0),
        EG3: (Number(prev.EG3) || 0) + (Number(curr.EG3) || 0),
        EG4: (Number(prev.EG4) || 0) + (Number(curr.EG4) || 0),
        EG5: (Number(prev.EG5) || 0) + (Number(curr.EG5) || 0),
        EG6: (Number(prev.EG6) || 0) + (Number(curr.EG6) || 0),
      }),{} as any);
      semesterTelemetry = {
        EG1: [...(semesterTelemetry.EG1 || []), Number(currentData?.EG1) || null],
        // EG2 is handled as negative -> export from my system
        EG2: [...(semesterTelemetry.EG2 || []), - Number(currentData?.EG2) || null],
        EG3: [...(semesterTelemetry.EG3 || []), Number(currentData?.EG3) || null],
        EG4: [...(semesterTelemetry.EG4 || []), Number(currentData?.EG4) || null],
        EG5: [...(semesterTelemetry.EG5 || []), - Number(currentData?.EG5) || null],
        // EG6 is handled as negative -> discharging from my battery
        EG6: [...(semesterTelemetry.EG6 || []), Number(currentData?.EG6) || null],
      };
    }

    const telemetryByMonth: EnergySeries = {};
    const telemetryByYear: EnergySeries = {};
    const telemetryAttributeNames = ['EG1', 'EG2', 'EG3', 'EG4', 'EG5' ,'EG6'];
    for (const name of telemetryAttributeNames) {
      const middleIndex = Math.ceil(semesterTelemetry[name].length / 2);
      const yearValues = [semesterTelemetry[name]]
      const values = [semesterTelemetry[name].slice(0, middleIndex), semesterTelemetry[name].slice(middleIndex)];
      telemetryByMonth[name] = {
        name,
        values,
        maxValue: values.map((value) => Math.max(...value)),
        totMin: Math.min(...semesterTelemetry[name]),
        totMax: Math.max(...semesterTelemetry[name]),
        totAvg: MathAvgWithValuesDifferentToZero(semesterTelemetry[name]),
      };

      telemetryByYear[name] = {
        name,
        values: yearValues,
        maxValue: yearValues.map((value) => Math.max(...value)),
        totMin: Math.min(...semesterTelemetry[name]),
        totMax: Math.max(...semesterTelemetry[name]),
        totAvg: MathAvgWithValuesDifferentToZero(semesterTelemetry[name]),
      };
    }

    this.semestersData = telemetryByMonth;
    this.yearOverviewData = telemetryByYear;
  }

  applyColors() {
    this.semestersData = applyColors(this.semestersData);
    this.yearOverviewData = applyColors(this.yearOverviewData);
  }

  getEnergies() {
    if (!this.semestersData) {
      throw new Error('Missing telemetry');
    }
    const selfConsume: Partial<EnergySeriesWithZeros> = {};
    const { EG1, EG3, EG4, EG6 } = removeNullToSeries(this.semestersData);
    EG1.values.forEach((_, i) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      selfConsume.consume === undefined ? (selfConsume.consume = initEnergySeries('consume')) : selfConsume.consume.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      selfConsume.selfConsume === undefined
        ? (selfConsume.selfConsume = initEnergySeries('selfConsume'))
        : selfConsume.selfConsume.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      selfConsume.fromSolar === undefined ? (selfConsume.fromSolar = initEnergySeries('fromSolar')) : selfConsume.fromSolar.values.push([]);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      selfConsume.fromBattery === undefined
        ? (selfConsume.fromBattery = initEnergySeries('fromBattery'))
        : selfConsume.fromBattery.values.push([]);
      _.forEach((__, j) => {
        const valueEG3 = EG3.values[i][j];
        const valueEG4 = EG4.values[i][j];
        const valueEG6 = EG6.values[i][j];
        if (selfConsume.consume && selfConsume.selfConsume && selfConsume.fromSolar && selfConsume.fromBattery) {
          selfConsume.consume.values[i][j] = valueEG4;
          selfConsume.selfConsume.values[i][j] = Number(valueEG4 || 0) - Number(valueEG3 || 0);
          selfConsume.fromSolar.values[i][j] = Number(valueEG4 || 0) - Number(valueEG3 || 0) - Number(valueEG6 || 0);
          selfConsume.fromBattery.values[i][j] = Number(valueEG6 || 0);
        }
      });
      selfConsume.consume.maxValue.push(Math.max(...selfConsume.consume.values[i]));
      selfConsume.selfConsume.maxValue.push(Math.max(...selfConsume.selfConsume.values[i]));
      selfConsume.fromSolar.maxValue.push(Math.max(...selfConsume.fromSolar.values[i]));
      selfConsume.fromBattery.maxValue.push(Math.max(...selfConsume.fromBattery.values[i]));
    });
    if (selfConsume.consume) {
      const flat = selfConsume.consume.values.reduce((prev, curr) => {
        const filtered = curr.filter((x) => x !== null);
        return [...prev, ...filtered];
      }, []) as number[];

      selfConsume.consume.totMax = Math.max(...selfConsume.consume.maxValue);
      selfConsume.consume.totMin = Math.min(...flat);
      selfConsume.consume.total = MathSomme(flat);
      selfConsume.consume.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }
    if (selfConsume.selfConsume) {
      const flat = selfConsume.selfConsume.values.reduce((prev, curr) => [...prev, ...curr], []);
      selfConsume.selfConsume.totMax = Math.max(...selfConsume.selfConsume.maxValue);
      selfConsume.selfConsume.total = MathSomme(flat);
      if (selfConsume.consume && selfConsume.consume.total) {
        selfConsume.selfConsume.selfConsumePercentage = Number((MathSomme(flat) /  selfConsume.consume.total).toFixed(2));
      }
      selfConsume.selfConsume.totMin = Math.min(...flat);
      selfConsume.selfConsume.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }
    if (selfConsume.fromSolar) {
      const flat = selfConsume.fromSolar.values.reduce((prev, curr) => [...prev, ...curr], []);
      selfConsume.fromSolar.totMax = Math.max(...selfConsume.fromSolar.maxValue);
      selfConsume.fromSolar.total = MathSomme(flat);
      if (selfConsume.selfConsume && selfConsume.selfConsume.total) {
        selfConsume.fromSolar.selfConsumePercentage = Number((MathSomme(flat) /  selfConsume.selfConsume.total).toFixed(2));
      }
      selfConsume.fromSolar.totMin = Math.min(...flat);
      selfConsume.fromSolar.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }
    if (selfConsume.fromBattery) {
      const flat = selfConsume.fromBattery.values.reduce((prev, curr) => [...prev, ...curr], []);
      selfConsume.fromBattery.totMax = Math.max(...selfConsume.fromBattery.maxValue);
      selfConsume.fromBattery.total = MathSomme(flat);
      if (selfConsume.selfConsume && selfConsume.selfConsume.total) {
        selfConsume.fromBattery.selfConsumePercentage = Number((MathSomme(flat) /  selfConsume.selfConsume.total).toFixed(2));
      }
      selfConsume.fromBattery.totMin = Math.min(...flat);
      selfConsume.fromBattery.totAvg = MathAvgWithValuesDifferentToZero(flat);
    }

    return applyEnergiesColors(selfConsume);
  }

  getMonthInWeeksSeries() {
    if (!this.semestersData) {
      throw new Error('Missing telemetry');
    }
    this.applyColors();
    return Object.values(this.semestersData);
  }

  getYearOverviewSeries() {
    if (!this.yearOverviewData) {
      throw new Error('Missing telemetry');
    }
    this.applyColors();
    return Object.values(this.yearOverviewData);
  }


  getGraphFnOptions(buildEnergyMonthGraph: any, buildEnergyYearGraph?: any) {
    return {
      chartOptionsFn: buildEnergyMonthGraph(this.getMonthInWeeksSeries(), this.periodDates),
      overviewChartOptionsFn: buildEnergyYearGraph ? buildEnergyYearGraph(this.getYearOverviewSeries(), this.yearDates) : undefined,
      series: this.semestersData,
    };
  }
}
